import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Field, useFormState } from 'react-final-form';
import deepEqual from 'deep-equal';

import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import WithError from '../../../../components/with-error/WithError';
import { Input } from '../../../../components/common-inputs';
import CommonButton, { BUTTON_STYLES } from '../../../../components/common-button/CommonButton';
import useGenieBrandsOptions from '../../../../hooks/useGenieBrandsOptions';
import CommonTextarea from '../../../../components/common-textarea/CommonTextarea';
import DataBlock from '../data-block/DataBlock';
import CommonGroupedDropdown from '../../../../components/common-grouped-dropdown/CommonGroupedDropdown';
import useAudienceOptions from '../../../hooks/useAudienceOptions';

import scss from './PredictForm.module.scss';

function PredictFormInner({ onSubmit, previousRequest }) {
  const { t } = useTranslation('genie');

  const brand = useSelector((state) => state.genie.brand);
  const isPredicting = useSelector((state) => state.genie.isPredicting);
  const isGeneratingAlternate = useSelector((state) => state.genie.isGeneratingAlternate);
  const isAudiencesLoading = useSelector((state) => state.genie.isAudiencesLoading);
  const predictedInitMessage = useSelector((state) => state.genie.predictedInitMessage);

  const audienceOptions = useAudienceOptions();
  const brandOptions = useGenieBrandsOptions();

  const { values } = useFormState();

  const selectedBrandLabel = brandOptions.find(({ value }) => value === brand)?.label || '';

  const isDirty = !deepEqual(previousRequest, values);

  return (
    <form className={scss.form} onSubmit={onSubmit}>
      <div className={scss.inputsBlock}>
        <div className={scss.inputsRow}>
          <WithLabel variant={LABEL_STYLES.ROW} label={`${t('brand')}:`} maxWidth={200} isBold>
            <Input type='text' size='sm' value={selectedBrandLabel} isReadOnly />
          </WithLabel>

          <Field name='url' autoComplete='genie-predict-url'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ROW} label='URL:' maxWidth={248} isBold isRequired>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <Input
                    type='text'
                    size='sm'
                    isError={meta.touched && meta.error}
                    formProps={input}
                    disabled={isPredicting}
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>

          <Field name='audienceId' autoComplete='genie-audienceId'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ROW} label={`${t('audience')}:`} maxWidth={360} isBold>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <CommonGroupedDropdown
                    placeholder={t('all')}
                    value={input.value}
                    groups={audienceOptions}
                    onSelect={input.onChange}
                    name={input.name}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    isError={meta.touched && meta.error}
                    disabled={isPredicting || isAudiencesLoading}
                    isLoading={isAudiencesLoading}
                    isShowGroupLabel
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>
        </div>

        <CommonButton
          type='submit'
          styleColor={BUTTON_STYLES.PRIMARY}
          maxWidth={180}
          className={scss.button}
          isLoading={isPredicting}
          disabled={!isDirty || isGeneratingAlternate}>
          {t('predictResponse')}
        </CommonButton>
      </div>

      <div className={scss.inputsBlock}>
        <Field name='message'>
          {({ meta, input }) => (
            <WithLabel
              variant={LABEL_STYLES.CUSTOM}
              label={`${t('message')}:`}
              isBold
              extraClassName={scss.textAreaField}
              maxWidth={600}
              isRequired>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
                <CommonTextarea
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={input.onBlur}
                  rows='2'
                  disabled={isPredicting}
                  isError={meta.touched && meta.error}
                  enableVoiceInput
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        {predictedInitMessage ? (
          <DataBlock
            r1={predictedInitMessage.interest}
            r2={predictedInitMessage.action}
            certainty={predictedInitMessage.uncertainty}
            error={predictedInitMessage.error}
          />
        ) : null}
      </div>
    </form>
  );
}

export default PredictFormInner;
